import { useState } from "react";
import { BatteryCharging, ChevronDown } from "lucide-react";

export const socAt = (pts, km) => {
  if (km <= pts[0].km) return pts[0].soc;
  for (let i = 1; i < pts.length; i++) {
    if (km <= pts[i].km) {
      const a = pts[i - 1];
      const b = pts[i];
      if (b.km === a.km) return b.soc;
      return a.soc + ((km - a.km) / (b.km - a.km)) * (b.soc - a.soc);
    }
  }
  return pts[pts.length - 1].soc;
};

export const buildSocPoints = (trip, day, start) => {
  const total = day.distance_km || 0;
  const buffer = trip.charge_buffer_pct || 15;
  const pts = [{ km: 0, soc: start }];
  const stops = (day.charging_stops || []).filter((c) => c.km_from_day_start != null).sort((a, b) => a.km_from_day_start - b.km_from_day_start);
  for (const c of stops) {
    pts.push({ km: Math.min(c.km_from_day_start, total), soc: c.arrival_soc ?? buffer, stop: true, name: (c.name || "Laadstop").split(",")[0] });
    pts.push({ km: Math.min(c.km_from_day_start, total), soc: c.charge_to_soc || 80 });
  }
  pts.push({ km: total, soc: day.arrival_soc ?? 0 });
  return pts;
};

export const SocChart = ({ trip, day, defaultSoc, liveKm = null }) => {
  const [open, setOpen] = useState(false);
  const start = day.start_soc_override ?? defaultSoc;
  const total = day.distance_km || 0;
  const buffer = trip.charge_buffer_pct || 15;
  if (!total) return null;
  const pts = buildSocPoints(trip, day, start);
  const W = 320, H = 104, padL = 26, padR = 8, padT = 10, padB = 16;
  const x = (km) => padL + (km / total) * (W - padL - padR);
  const y = (soc) => padT + (1 - Math.max(Math.min(soc, 100), 0) / 100) * (H - padT - padB);
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.km).toFixed(1)},${y(p.soc).toFixed(1)}`).join(" ");
  const area = `${line} L${x(total).toFixed(1)},${y(0).toFixed(1)} L${x(0).toFixed(1)},${y(0).toFixed(1)} Z`;
  const lkm = liveKm != null ? Math.max(0, Math.min(liveKm, total)) : null;
  const liveSoc = lkm != null ? socAt(pts, lkm) : null;
  return (
    <div data-testid={`soc-chart-${day.day_number}`} className="mb-4 bg-secondary/40 rounded-xl overflow-hidden">
      <button type="button" data-testid={`soc-chart-toggle-${day.day_number}`} onClick={() => setOpen(!open)}
        title={open ? "Batterijverloop verbergen" : "Batterijverloop bekijken"}
        className="w-full flex items-center gap-1.5 px-3 py-2 text-left hover:bg-secondary/60 transition-[background-color] duration-200">
        <BatteryCharging className="w-3.5 h-3.5 text-[#52B788] shrink-0" />
        <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Batterijverloop</span>
        {liveSoc != null && (
          <span data-testid={`soc-live-label-${day.day_number}`} className="inline-flex items-center gap-1 font-mono text-[10px] text-[#52B788] font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-[#52B788] animate-pulse" /> nu ~{Math.round(liveSoc)}%
          </span>
        )}
        <span className="font-mono text-[10px] text-muted-foreground ml-auto shrink-0">{start}% → ~{day.arrival_soc}%</span>
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
      <div className="px-3 pb-3">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Batterijverloop van deze dag">
        <defs>
          <linearGradient id={`socg-${day.day_number}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#52B788" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#52B788" stopOpacity="0.03" />
          </linearGradient>
        </defs>
        {[0, 50, 100].map((v) => (
          <g key={v}>
            <line x1={padL} y1={y(v)} x2={W - padR} y2={y(v)} stroke="currentColor" strokeOpacity="0.08" />
            <text x={padL - 4} y={y(v) + 3} textAnchor="end" fontSize="8" fill="currentColor" opacity="0.45" fontFamily="monospace">{v}</text>
          </g>
        ))}
        <line x1={padL} y1={y(buffer)} x2={W - padR} y2={y(buffer)} stroke="#E07A5F" strokeDasharray="3 3" strokeWidth="1" opacity="0.7" />
        <text x={W - padR} y={y(buffer) - 3} textAnchor="end" fontSize="7.5" fill="#E07A5F" fontFamily="monospace">{`buffer ${buffer}%`}</text>
        <path d={area} fill={`url(#socg-${day.day_number})`} />
        <path d={line} fill="none" stroke="#52B788" strokeWidth="2" strokeLinejoin="round" />
        {pts.filter((p) => p.stop).map((p, i) => (
          <g key={i}>
            <circle cx={x(p.km)} cy={y(p.soc)} r="3.5" fill="#F59E0B" stroke="white" strokeWidth="1.2" />
            <title>{p.name} — aankomst met {Math.round(p.soc)}%</title>
          </g>
        ))}
        {lkm != null && (
          <g data-testid={`soc-live-dot-${day.day_number}`}>
            <circle cx={x(lkm)} cy={y(liveSoc)} r="8" fill="#52B788" opacity="0.25">
              <animate attributeName="r" values="6;11;6" dur="2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.35;0.08;0.35" dur="2s" repeatCount="indefinite" />
            </circle>
            <circle cx={x(lkm)} cy={y(liveSoc)} r="4" fill="#52B788" stroke="white" strokeWidth="1.5" />
            <text x={lkm > total * 0.72 ? x(lkm) - 8 : x(lkm) + 8} y={Math.max(y(liveSoc) - 7, 9)}
              textAnchor={lkm > total * 0.72 ? "end" : "start"} fontSize="9" fontWeight="700" fill="#52B788" fontFamily="monospace">
              {`nu ~${Math.round(liveSoc)}%`}
            </text>
          </g>
        )}
        <text x={padL} y={H - 3} fontSize="8" fill="currentColor" opacity="0.45" fontFamily="monospace">0 km</text>
        <text x={W - padR} y={H - 3} textAnchor="end" fontSize="8" fill="currentColor" opacity="0.45" fontFamily="monospace">{`${Math.round(total)} km`}</text>
      </svg>
      </div>
      )}
    </div>
  );
};
